import { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { format, subMonths, addMonths, startOfMonth } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  ChevronLeft, ChevronRight, Download, TrendingUp, Users,
  Clock, AlertTriangle, BarChart3, FileWarning, Calendar,
  Building2, Briefcase, UserCheck, ShoppingCart, Plus
} from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'
import { useModules } from '../lib/modules'

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
const CONTRACT_COLORS: Record<string, string> = { CDI: '#3B82F6', CDD: '#F59E0B', Extra: '#8B5CF6', Stage: '#6B7280' }

type Tab = 'overview' | 'planned_vs_real' | 'positions' | 'absenteeism' | 'hr' | 'revenue'

function KPI({ label, value, sub, color, icon: Icon }: { label: string; value: string; sub?: string; color?: string; icon?: any }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-center gap-3 mb-2">
        {Icon && <div className="bg-gray-100 p-2 rounded-lg"><Icon size={16} className={color || 'text-gray-500'} /></div>}
        <span className="text-xs text-gray-500 font-medium">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function MiniBar({ label, value, max, color, suffix = '' }: { label: string; value: number; max: number; color: string; suffix?: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div className="flex items-center gap-3">
      <div className="w-32 text-xs text-gray-600 text-right flex-shrink-0 truncate">{label}</div>
      <div className="flex-1 bg-gray-100 rounded-full h-4 relative overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="w-20 text-xs text-gray-700 font-medium text-right">{typeof value === 'number' ? value.toFixed(1) : value}{suffix}</div>
    </div>
  )
}

function ScoreCircle({ score }: { score: number }) {
  const color = score >= 80 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444'
  return (
    <span className="inline-flex items-center justify-center w-12 h-6 rounded-full text-white text-xs font-bold" style={{ backgroundColor: color }}>
      {score}%
    </span>
  )
}

export default function Reporting() {
  const now = new Date()
  const [monthDate, setMonthDate] = useState(() => startOfMonth(now))
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const { modules, loaded: modulesLoaded } = useModules()
  // Module RH éteint : seul l'onglet CA reste (les autres parlent heures, coûts, absences)
  useEffect(() => { if (modulesLoaded && !modules.rh) setActiveTab('revenue') }, [modulesLoaded, modules.rh])
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const { current: establishment, establishments } = useEstablishment()
  const [revenueModal, setRevenueModal] = useState(false)
  const [revForm, setRevForm] = useState({ date: '', revenue: '', note: '' })
  const [savingRev, setSavingRev] = useState(false)
  const [bulkCSV, setBulkCSV] = useState('')
  const [showBulk, setShowBulk] = useState(false)
  const [xlsxImporting, setXlsxImporting] = useState(false)

  const monthStr = format(monthDate, 'yyyy-MM')

  useEffect(() => {
    if (!establishment) return
    setLoading(true)
    api.get(`/reporting?month=${monthStr}&establishmentId=${establishment.id}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false))
  }, [monthStr, establishment])

  async function saveRevenue() {
    if (!establishment || !revForm.date || !revForm.revenue) return
    setSavingRev(true)
    try {
      await api.post('/revenue', { establishmentId: establishment.id, date: revForm.date, revenue: parseFloat(revForm.revenue), note: revForm.note || undefined })
      setRevenueModal(false)
      setRevForm({ date: '', revenue: '', note: '' })
      // Reload
      const r = await api.get(`/reporting?month=${monthStr}&establishmentId=${establishment.id}`)
      setData(r.data)
    } finally { setSavingRev(false) }
  }

  async function importXlsx(file: File) {
    if (!establishment) return
    setXlsxImporting(true)
    try {
      const buffer = await file.arrayBuffer()
      const wb = XLSX.read(buffer, { type: 'array' })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rawRows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })

      function parseDate(cell: any): string | null {
        if (!cell) return null
        const s = String(cell).trim()
        const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/)
        if (m) return `${m[3]}-${m[2]}-${m[1]}`
        if (typeof cell === 'number') {
          const d = XLSX.SSF.parse_date_code(cell)
          if (d) return `${d.y}-${String(d.m).padStart(2,'0')}-${String(d.d).padStart(2,'0')}`
        }
        return null
      }

      const rows: { date: string; revenue: number; clients?: number; panierMoyen?: number }[] = []
      for (let i = 1; i < rawRows.length; i++) {
        const r = rawRows[i]
        if (!r) continue
        // N : col A (0)=date, B (1)=CA, C (2)=clients, D (3)=panier moy
        const dN = parseDate(r[0]); const caN = parseFloat(String(r[1] ?? '').replace(',', '.'))
        const clientsN = parseInt(String(r[2] ?? '').replace(',', '.'))
        const panierN = parseFloat(String(r[3] ?? '').replace(',', '.'))
        if (dN && !isNaN(caN) && caN > 0) rows.push({
          date: dN, revenue: caN,
          ...(clientsN > 0 && !isNaN(clientsN) ? { clients: clientsN } : {}),
          ...(!isNaN(panierN) && panierN > 0 ? { panierMoyen: panierN } : {})
        })
        // N-1 : col I (8)=date, J (9)=CA, K (10)=clients, L (11)=panier moy
        const dN1 = parseDate(r[8]); const caN1 = parseFloat(String(r[9] ?? '').replace(',', '.'))
        const clientsN1 = parseInt(String(r[10] ?? '').replace(',', '.'))
        const panierN1 = parseFloat(String(r[11] ?? '').replace(',', '.'))
        if (dN1 && !isNaN(caN1) && caN1 > 0) rows.push({
          date: dN1, revenue: caN1,
          ...(clientsN1 > 0 && !isNaN(clientsN1) ? { clients: clientsN1 } : {}),
          ...(!isNaN(panierN1) && panierN1 > 0 ? { panierMoyen: panierN1 } : {})
        })
      }

      if (!rows.length) { alert('Aucune donnée trouvée dans le fichier'); return }
      await api.post('/revenue/bulk', { establishmentId: establishment.id, rows })
      alert(`Import réussi : ${rows.length} jours importés (N et N-1)`)
      const r2 = await api.get(`/reporting?month=${format(monthDate, 'yyyy-MM')}&establishmentId=${establishment.id}`)
      setData(r2.data)
    } catch (e: any) {
      alert('Erreur import : ' + (e.response?.data?.error || e.message))
    } finally { setXlsxImporting(false) }
  }

  async function bulkImport() {
    if (!establishment) return
    const lines = bulkCSV.trim().split('\n').map(l => l.trim()).filter(Boolean)
    const rows = lines.map(l => {
      const [date, revenue, note] = l.split(';')
      return { date: date?.trim(), revenue: parseFloat(revenue?.trim()), note: note?.trim() }
    }).filter(r => r.date && !isNaN(r.revenue))
    if (!rows.length) { alert('Aucune ligne valide. Format: YYYY-MM-DD;montant;note'); return }
    await api.post('/revenue/bulk', { establishmentId: establishment.id, rows })
    setShowBulk(false); setBulkCSV('')
    const r = await api.get(`/reporting?month=${monthStr}&establishmentId=${establishment.id}`)
    setData(r.data)
  }

  function exportCSV() {
    if (!data) return
    const rows = [
      ['Employé', 'Contrat', 'H. planifiées', 'H. réalisées', 'Écart', 'Absences', 'Retards', 'Coût brut'],
      ...data.empSummary.map((e: any) => [
        `${e.firstName} ${e.lastName}`, e.contractType,
        e.planned.toFixed(1), e.realized.toFixed(1), e.delta.toFixed(1),
        e.absenceDays, e.lateCount, e.cost.toFixed(2)
      ])
    ]
    const csv = rows.map((r: any[]) => r.join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob); const a = document.createElement('a')
    a.href = url; a.download = `reporting-${monthStr}.csv`; a.click(); URL.revokeObjectURL(url)
  }

  const TABS = ([
    { key: 'overview',       label: 'Vue d\'ensemble',   icon: BarChart3 },
    { key: 'planned_vs_real',label: 'Prévu vs Réalisé',  icon: Clock },
    { key: 'positions',      label: 'Postes & Coûts',    icon: Briefcase },
    { key: 'absenteeism',    label: 'Absences & Retards',icon: AlertTriangle },
    { key: 'hr',             label: 'Dossiers RH',       icon: UserCheck },
    { key: 'revenue',        label: 'CA & Productivité', icon: ShoppingCart },
  ] as { key: Tab; label: string; icon: any }[]).filter(t => modules.rh || t.key === 'revenue')

  if (!establishment) return (
    <div className="p-6 text-center text-gray-400">Sélectionnez un établissement</div>
  )

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-indigo-600" /> Reporting & Pilotage
          </h1>
          <p className="text-sm text-gray-400 mt-0.5">{establishment.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border rounded-lg shadow-sm">
            <button onClick={() => setMonthDate(d => startOfMonth(subMonths(d, 1)))} className="p-2 hover:bg-gray-50 rounded-l-lg"><ChevronLeft size={18} /></button>
            <span className="px-4 text-sm font-medium text-gray-700 capitalize whitespace-nowrap">{format(monthDate, 'MMMM yyyy', { locale: fr })}</span>
            <button onClick={() => setMonthDate(d => startOfMonth(addMonths(d, 1)))} className="p-2 hover:bg-gray-50 rounded-r-lg"><ChevronRight size={18} /></button>
          </div>
          <button onClick={() => setMonthDate(startOfMonth(new Date()))} className="text-sm text-blue-600 hover:text-blue-800">Ce mois</button>
          <button onClick={exportCSV} disabled={!data} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg disabled:opacity-40">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeTab === key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-24 text-gray-400">Chargement...</div>
      ) : !data ? null : (
        <>
          {/* ── Overview ─────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KPI label="Heures planifiées" value={`${data.empSummary.reduce((s: number, e: any) => s + e.planned, 0).toFixed(0)}h`} sub="shifts du mois" color="text-blue-600" icon={Clock} />
                <KPI label="Heures réalisées" value={`${data.empSummary.reduce((s: number, e: any) => s + e.realized, 0).toFixed(0)}h`} sub="feuilles validées" color="text-green-600" icon={Clock} />
                <KPI label="Masse brute" value={`${data.empSummary.reduce((s: number, e: any) => s + e.cost, 0).toFixed(0)} €`} sub="hors charges pat." color="text-teal-600" icon={TrendingUp} />
                <KPI label="Coût employeur" value={`${data.empSummary.reduce((s: number, e: any) => s + e.employerCost, 0).toFixed(0)} €`} sub="charges incluses ~45%" color="text-gray-700" icon={TrendingUp} />
                <KPI label="Taux d'absence" value={`${data.absenceStats.rate.toFixed(1)}%`} sub={`${data.absenceStats.totalDays} jours`} color={data.absenceStats.rate > 5 ? 'text-red-600' : 'text-gray-900'} icon={AlertTriangle} />
                <KPI label="H. supplémentaires" value={`${data.otStats.totalHours.toFixed(1)}h`} sub="total mois" color="text-amber-600" icon={Clock} />
                <KPI label="Retards" value={`${data.latenessStats.totalEvents}`} sub={`moy. ${data.latenessStats.avgMinutes} min`} color={data.latenessStats.totalEvents > 5 ? 'text-orange-600' : 'text-gray-900'} icon={AlertTriangle} />
                <KPI label="Fins de contrat (90j)" value={`${data.upcomingEnds.length}`} sub="CDD / Extras" color={data.upcomingEnds.length > 0 ? 'text-orange-600' : 'text-gray-900'} icon={Calendar} />
              </div>

              {/* Répartition contrats + coût/site */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Briefcase size={15} /> Répartition des contrats</h2>
                  <div className="space-y-3">
                    {Object.entries(data.contractDist as Record<string, number>).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CONTRACT_COLORS[type] || '#9CA3AF' }} />
                          <span className="text-sm text-gray-700">{type}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-800">{count} pers.</span>
                      </div>
                    ))}
                    {Object.keys(data.contractDist).length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucune donnée</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Building2 size={15} /> Coût par établissement</h2>
                  <div className="space-y-2.5">
                    {data.costByEstab.map((e: any) => (
                      <MiniBar key={e.name} label={e.name} value={e.totalCost}
                        max={Math.max(...data.costByEstab.map((x: any) => x.totalCost), 1)}
                        color="#6366F1" suffix=" €" />
                    ))}
                    {data.costByEstab.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucune donnée</p>}
                  </div>
                </div>
              </div>

              {/* Upcoming contract ends */}
              {data.upcomingEnds.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-5">
                  <h2 className="font-semibold text-orange-800 mb-3 flex items-center gap-2"><Calendar size={15} /> Fins de contrat à venir (90 jours)</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.upcomingEnds.map((emp: any) => (
                      <div key={emp.id} className="bg-white rounded-lg p-3 border border-orange-100">
                        <div className="font-medium text-gray-900">{emp.firstName} {emp.lastName}</div>
                        <div className="text-xs text-gray-500">{emp.contractType} — {emp.establishment}</div>
                        <div className={`text-xs font-semibold mt-1 ${emp.daysLeft <= 14 ? 'text-red-600' : emp.daysLeft <= 30 ? 'text-orange-600' : 'text-amber-600'}`}>
                          {emp.daysLeft}j — expire le {emp.contractEndDate}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Planned vs Realized ──────────────────────────── */}
          {activeTab === 'planned_vs_real' && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">Heures prévues vs réalisées</h2>
                <div className="flex gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-400" /> Planifié</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-400" /> Réalisé</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Employé</th>
                      <th className="px-4 py-3 text-center">Contrat</th>
                      <th className="px-4 py-3 text-center">H. contract./mois</th>
                      <th className="px-4 py-3 text-center text-blue-600">Planifiées</th>
                      <th className="px-4 py-3 text-center text-green-600">Réalisées</th>
                      <th className="px-4 py-3 text-center">Écart</th>
                      <th className="px-4 py-3 text-center">Absences</th>
                      <th className="px-4 py-3 text-center">Retards</th>
                      <th className="px-4 py-3 text-right">Coût brut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.empSummary.map((emp: any) => {
                      const contractMonthly = emp.weeklyHours * 4.33
                      const delta = emp.realized - emp.planned
                      return (
                        <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{emp.firstName} {emp.lastName}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: CONTRACT_COLORS[emp.contractType] || '#9CA3AF' }}>{emp.contractType}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-500 text-xs">{contractMonthly.toFixed(0)}h</td>
                          <td className="px-4 py-3 text-center font-semibold text-blue-600">{emp.planned.toFixed(1)}h</td>
                          <td className="px-4 py-3 text-center font-semibold text-green-600">{emp.realized.toFixed(1)}h</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs font-bold ${delta > 2 ? 'text-orange-600' : delta < -2 ? 'text-red-500' : 'text-gray-400'}`}>
                              {delta >= 0 ? '+' : ''}{delta.toFixed(1)}h
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{emp.absenceDays > 0 ? `${emp.absenceDays}j` : '—'}</td>
                          <td className="px-4 py-3 text-center">
                            {emp.lateCount > 0 ? <span className="text-orange-600 font-medium">{emp.lateCount}</span> : '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800">{emp.cost.toFixed(2)} €</td>
                        </tr>
                      )
                    })}
                    {data.empSummary.length === 0 && (
                      <tr><td colSpan={9} className="text-center py-10 text-gray-400">Aucune donnée</td></tr>
                    )}
                  </tbody>
                  {data.empSummary.length > 0 && (
                    <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 font-semibold text-gray-700">Total</td>
                        <td className="px-4 py-3 text-center font-bold text-blue-600">{data.empSummary.reduce((s: number, e: any) => s + e.planned, 0).toFixed(1)}h</td>
                        <td className="px-4 py-3 text-center font-bold text-green-600">{data.empSummary.reduce((s: number, e: any) => s + e.realized, 0).toFixed(1)}h</td>
                        <td colSpan={3} />
                        <td className="px-4 py-3 text-right font-bold text-blue-600">{data.empSummary.reduce((s: number, e: any) => s + e.cost, 0).toFixed(2)} €</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* ── Positions & Costs ─────────────────────────────── */}
          {activeTab === 'positions' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4">Coût moyen par poste</h2>
                  <div className="space-y-3">
                    {data.costByPosition.map((pos: any) => (
                      <div key={pos.name}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{pos.name}</span>
                          <span className="text-gray-500 text-xs">{pos.empCount} pers. · {pos.totalHours.toFixed(0)}h</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div className="h-3 bg-indigo-400 rounded-full" style={{ width: `${Math.min(100, (pos.totalCost / Math.max(...data.costByPosition.map((p: any) => p.totalCost), 1)) * 100)}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-gray-700 w-20 text-right">{pos.totalCost.toFixed(0)} €</span>
                        </div>
                      </div>
                    ))}
                    {data.costByPosition.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucune donnée</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4">Coût par établissement</h2>
                  <div className="space-y-3">
                    {data.costByEstab.map((estab: any) => (
                      <div key={estab.name}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{estab.name}</span>
                          <span className="text-gray-500 text-xs">{estab.empCount} pers.</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-3">
                            <div className="h-3 bg-purple-400 rounded-full" style={{ width: `${Math.min(100, (estab.totalCost / Math.max(...data.costByEstab.map((e: any) => e.totalCost), 1)) * 100)}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-gray-700 w-20 text-right">{estab.totalCost.toFixed(0)} €</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">{estab.totalHours.toFixed(0)}h · coût employeur ≈ {(estab.totalCost * 1.45).toFixed(0)} €</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Répartition contrats détail */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="font-semibold text-gray-800 mb-4">Répartition des contrats</h2>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(data.contractDist as Record<string, number>).map(([type, count]) => {
                    const total = Object.values(data.contractDist as Record<string, number>).reduce((s, n) => s + n, 0)
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0
                    return (
                      <div key={type} className="flex-1 min-w-[120px] bg-gray-50 rounded-xl p-4 text-center border border-gray-100">
                        <div className="text-3xl font-bold" style={{ color: CONTRACT_COLORS[type] || '#9CA3AF' }}>{count}</div>
                        <div className="text-sm font-medium text-gray-700 mt-1">{type}</div>
                        <div className="text-xs text-gray-400">{pct}% des effectifs</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Absenteeism & Lateness ───────────────────────── */}
          {activeTab === 'absenteeism' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPI label="Taux d'absentéisme" value={`${data.absenceStats.rate.toFixed(1)}%`} sub={`${data.absenceStats.totalDays} jours d'absence`} color={data.absenceStats.rate > 5 ? 'text-red-600' : data.absenceStats.rate > 2 ? 'text-orange-500' : 'text-green-600'} icon={AlertTriangle} />
                <KPI label="Événements retard" value={`${data.latenessStats.totalEvents}`} sub={`${data.latenessStats.totalMinutes} min cumulées`} color={data.latenessStats.totalEvents > 5 ? 'text-orange-600' : 'text-gray-900'} icon={Clock} />
                <KPI label="Retard moyen" value={`${data.latenessStats.avgMinutes} min`} sub="par événement" color="text-gray-700" icon={Clock} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4">Absences par type</h2>
                  <div className="space-y-2.5">
                    {data.absenceStats.byType.map((t: any) => (
                      <div key={t.code} className="flex items-center gap-3">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.color }} />
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between text-sm mb-0.5">
                            <span className="text-gray-700 truncate">{t.name}</span>
                            <span className="text-gray-500 text-xs flex-shrink-0 ml-2">{t.count} dem. · {t.days}j</span>
                          </div>
                          <div className="bg-gray-100 rounded-full h-2">
                            <div className="h-2 rounded-full" style={{ width: `${Math.min(100, (t.days / Math.max(data.absenceStats.totalDays, 1)) * 100)}%`, backgroundColor: t.color }} />
                          </div>
                        </div>
                      </div>
                    ))}
                    {data.absenceStats.byType.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucune absence approuvée ce mois</p>}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h2 className="font-semibold text-gray-800 mb-4">Retards par employé</h2>
                  <div className="space-y-2">
                    {data.latenessStats.byEmployee.sort((a: any, b: any) => b.totalMins - a.totalMins).map((emp: any, i: number) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{emp.firstName} {emp.lastName}</span>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-orange-600">{emp.count}x</span>
                          <span className="text-xs text-gray-400 ml-2">{emp.totalMins} min</span>
                        </div>
                      </div>
                    ))}
                    {data.latenessStats.byEmployee.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Aucun retard ce mois</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── HR Quality ───────────────────────────────────── */}
          {activeTab === 'hr' && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                {['Complet (≥80%)', 'Partiel (50-79%)', 'Incomplet (<50%)'].map((label, i) => {
                  const thresholds = [[80, 101], [50, 80], [0, 50]]
                  const count = data.hrQuality.filter((e: any) => e.score >= thresholds[i][0] && e.score < thresholds[i][1]).length
                  const colors = ['text-green-600', 'text-orange-500', 'text-red-600']
                  return <KPI key={label} label={label} value={`${count} dossier${count > 1 ? 's' : ''}`} color={colors[i]} icon={FileWarning} />
                })}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100">
                  <h2 className="font-semibold text-gray-800">Qualité des dossiers RH</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Basé sur : email, téléphone, date naissance, N° SS, IBAN, début contrat, urgence, adresse, poste, etc.</p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left">Employé</th>
                      <th className="px-4 py-3 text-left">Établissement</th>
                      <th className="px-4 py-3 text-center">Score</th>
                      <th className="px-4 py-3 text-left">Champs manquants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.hrQuality.map((emp: any) => (
                      <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{emp.firstName} {emp.lastName}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{emp.establishment}</td>
                        <td className="px-4 py-3 text-center"><ScoreCircle score={emp.score} /></td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {emp.missing.map((m: string) => (
                              <span key={m} className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">{m}</span>
                            ))}
                            {emp.missing.length === 0 && <span className="text-xs text-green-600">✓ Complet</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Upcoming ends */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800">Fins de contrat à venir</h2>
                  <span className="text-xs text-gray-400">90 jours glissants</span>
                </div>
                {data.upcomingEnds.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">Aucune fin de contrat prévue dans les 90 prochains jours</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-4 py-3 text-left">Employé</th>
                        <th className="px-4 py-3 text-center">Type</th>
                        <th className="px-4 py-3 text-center">Date fin</th>
                        <th className="px-4 py-3 text-center">Jours restants</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.upcomingEnds.map((emp: any) => (
                        <tr key={emp.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{emp.firstName} {emp.lastName}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: CONTRACT_COLORS[emp.contractType] || '#9CA3AF' }}>{emp.contractType}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">{emp.contractEndDate}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-sm font-bold ${emp.daysLeft <= 14 ? 'text-red-600' : emp.daysLeft <= 30 ? 'text-orange-600' : 'text-amber-500'}`}>
                              {emp.daysLeft}j
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ── Revenue & Productivity ───────────────────────── */}
          {activeTab === 'revenue' && (
            <div className="space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <ShoppingCart size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Module CA optionnel</p>
                  <p className="text-xs text-blue-600 mt-0.5">Saisissez votre CA journalier pour calculer le ratio masse salariale/CA et piloter votre productivité.</p>
                </div>
              </div>

              {/* Summary KPIs */}
              {data.revenueStats.totalRevenue > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <KPI label={`CA ${monthDate.getFullYear()}`} value={`${data.revenueStats.totalRevenue.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}`} color="text-green-600" icon={TrendingUp} />
                  {data.revenueStats.totalRevenueN1 > 0 && (
                    <KPI label={`CA ${monthDate.getFullYear()-1}`} value={`${data.revenueStats.totalRevenueN1.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}`} color="text-gray-500" icon={TrendingUp}
                      sub={data.revenueStats.totalRevenueN1 > 0 ? `${((data.revenueStats.totalRevenue / data.revenueStats.totalRevenueN1 - 1) * 100).toFixed(1)}% vs N-1` : ''} />
                  )}
                  <KPI label="Masse salariale" value={`${data.revenueStats.totalPayroll.toFixed(0)} €`} color="text-blue-600" icon={TrendingUp} />
                  <KPI label="Ratio MS/CA" value={data.revenueStats.ratioPayrollRevenue ? `${data.revenueStats.ratioPayrollRevenue.toFixed(1)}%` : '—'}
                    sub="idéal < 30% commerce" color={data.revenueStats.ratioPayrollRevenue > 35 ? 'text-red-600' : 'text-green-600'} icon={BarChart3} />
                </div>
              )}

              {/* Revenue table N vs N-1 */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-800">CA journalier — comparaison N / N-1</h2>
                  <div className="flex gap-2">
                    <label className={`text-xs cursor-pointer border px-2 py-1 rounded-lg ${xlsxImporting ? 'text-gray-400 border-gray-100' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                      {xlsxImporting ? 'Import...' : 'Import Excel'}
                      <input type="file" accept=".xlsx,.xls" className="hidden" disabled={xlsxImporting}
                        onChange={e => { if (e.target.files?.[0]) importXlsx(e.target.files[0]); e.target.value = '' }} />
                    </label>
                    <button onClick={() => setShowBulk(!showBulk)} className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 px-2 py-1 rounded-lg">
                      Import CSV
                    </button>
                    <button onClick={() => setRevenueModal(true)} className="flex items-center gap-1 text-xs text-white bg-green-600 hover:bg-green-700 px-3 py-1.5 rounded-lg font-medium">
                      <Plus size={13} /> Saisir CA
                    </button>
                  </div>
                </div>

                {showBulk && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <p className="text-xs text-gray-500 mb-2">Format : <code>YYYY-MM-DD;montant;note (optionnel)</code>, une ligne par jour</p>
                    <textarea value={bulkCSV} onChange={e => setBulkCSV(e.target.value)} rows={5}
                      placeholder="2025-03-01;4580.50;Samedi&#10;2025-03-02;3210.00;Dimanche"
                      className="w-full border border-gray-200 rounded-lg p-2 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    <button onClick={bulkImport} className="mt-2 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg">Importer</button>
                  </div>
                )}

                {data.revenueStats.revenues.length === 0 && data.revenueStats.revenuesN1?.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-10">Aucun CA saisi pour ce mois.<br />Cliquez sur "Import Excel" ou "Saisir CA" pour commencer.</p>
                ) : (() => {
                  const n1Map: Record<string, any> = {}
                  ;(data.revenueStats.revenuesN1 || []).forEach((r: any) => { n1Map[r.date] = r })
                  function getN1Date(dateStr: string): string {
                    const d = new Date(dateStr + 'T12:00:00')
                    d.setDate(d.getDate() - 364)
                    return d.toISOString().slice(0, 10)
                  }
                  const maxRev = Math.max(...data.revenueStats.revenues.map((r: any) => r.revenue), 1)
                  return (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="text-left py-2 text-gray-500 font-medium w-24">Date</th>
                            <th className="text-right py-2 text-green-700 font-medium">CA {monthDate.getFullYear()}</th>
                            <th className="text-right py-2 text-gray-500 font-medium">Clients</th>
                            <th className="text-right py-2 text-gray-500 font-medium">Panier</th>
                            <th className="text-right py-2 text-gray-400 font-medium">Date N-1</th>
                            <th className="text-right py-2 text-gray-400 font-medium">CA {monthDate.getFullYear()-1}</th>
                            <th className="text-right py-2 text-gray-400 font-medium">Clients</th>
                            <th className="text-right py-2 text-gray-400 font-medium">Panier</th>
                            <th className="text-right py-2 text-blue-600 font-medium">Évol.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.revenueStats.revenues.map((r: any) => {
                            const rN1 = n1Map[getN1Date(r.date)]
                            const evol = rN1 ? ((r.revenue / rN1.revenue - 1) * 100) : null
                            return (
                              <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="py-1.5 text-gray-600">{new Date(r.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                                <td className="py-1.5 text-right font-semibold text-gray-800">{r.revenue.toLocaleString('fr-FR')} €</td>
                                <td className="py-1.5 text-right text-gray-600">{r.clients ?? '—'}</td>
                                <td className="py-1.5 text-right text-gray-600">{r.panierMoyen ? r.panierMoyen.toFixed(2) + ' €' : '—'}</td>
                                <td className="py-1.5 text-right text-gray-400 text-[11px]">{rN1 ? new Date(rN1.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}</td>
                                <td className="py-1.5 text-right text-gray-400">{rN1 ? rN1.revenue.toLocaleString('fr-FR') + ' €' : '—'}</td>
                                <td className="py-1.5 text-right text-gray-400">{rN1?.clients ?? '—'}</td>
                                <td className="py-1.5 text-right text-gray-400">{rN1?.panierMoyen ? rN1.panierMoyen.toFixed(2) + ' €' : '—'}</td>
                                <td className={`py-1.5 text-right font-medium ${evol === null ? 'text-gray-300' : evol >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                  {evol !== null ? `${evol >= 0 ? '+' : ''}${evol.toFixed(1)}%` : '—'}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="border-t border-gray-200 font-semibold">
                            <td className="py-2 text-gray-700">Total</td>
                            <td className="py-2 text-right text-green-700">{data.revenueStats.totalRevenue.toLocaleString('fr-FR')} €</td>
                            <td colSpan={2} />
                            <td className="py-2 text-right text-gray-400">{data.revenueStats.totalRevenueN1 > 0 ? data.revenueStats.totalRevenueN1.toLocaleString('fr-FR') + ' €' : '—'}</td>
                            <td colSpan={2} />
                            <td className={`py-2 text-right ${data.revenueStats.totalRevenueN1 > 0 ? (data.revenueStats.totalRevenue >= data.revenueStats.totalRevenueN1 ? 'text-green-600' : 'text-red-500') : 'text-gray-300'}`}>
                              {data.revenueStats.totalRevenueN1 > 0 ? `${((data.revenueStats.totalRevenue / data.revenueStats.totalRevenueN1 - 1) * 100).toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )
                })()}
              </div>
            </div>
          )}
        </>
      )}

      {/* Revenue modal */}
      {revenueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setRevenueModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Saisir le CA</h2>
              <button onClick={() => setRevenueModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" value={revForm.date} onChange={e => setRevForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CA journalier (€) *</label>
                <input type="number" step="0.01" value={revForm.revenue} onChange={e => setRevForm(f => ({ ...f, revenue: e.target.value }))}
                  placeholder="4500.00" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnel)</label>
                <input value={revForm.note} onChange={e => setRevForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="Samedi, événement..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setRevenueModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={saveRevenue} disabled={savingRev || !revForm.date || !revForm.revenue}
                className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg">
                {savingRev ? '...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
